/**
 * Student ID Configuration panel — iter-67. Lives inside the Branding
 * tab of Settings, directly below Employee Number config. Owner/admin
 * only. Live preview updates as the admin types.
 *
 * Two scopes:
 *   • Workspace default — applied to any branch that doesn't override.
 *   • Per-branch override — pick a branch from the dropdown, edit,
 *     save. "Reset to workspace default" deletes the override.
 *
 * Generated Student IDs are ALWAYS workspace-unique — the per-branch
 * setting only changes the FORMAT (prefix / separator / digits /
 * start-number). The number space is one continuous counter per
 * workspace, so branches never collide with each other.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { IdentificationBadge, ArrowsClockwise, GraduationCap, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const SEP_OPTIONS = [
  { value: "-",    label: "Hyphen  (SUN-0001)" },
  { value: "/",    label: "Slash  (SUN/0001)" },
  { value: "none", label: "None  (SUN0001)" },  // Shadcn Select can't accept ""
];

const DEFAULT_CFG = {
  prefix: "STU",
  separator: "-",
  digits: 4,
  include_year: false,
  start_number: 1,
  auto_generate: true,
};

function formatPreview(cfg, seq) {
  const year = new Date().getFullYear();
  const pad = String(seq).padStart(Math.max(1, Math.min(8, cfg.digits || 1)), "0");
  const parts = [];
  if (cfg.prefix) parts.push(cfg.prefix);
  if (cfg.include_year) parts.push(String(year));
  parts.push(pad);
  return cfg.separator ? parts.join(cfg.separator) : parts.join("");
}

export default function StudentNumberConfigPanel() {
  const { user } = useAuth();
  const canEdit = (user?.roles || []).some(r => r === "owner" || r === "admin");
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [scope, setScope] = useState("workspace"); // "workspace" | branch_id
  const [isOverride, setIsOverride] = useState(false);
  const [branches, setBranches] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nextNumber, setNextNumber] = useState(null);

  // Load branches once — for the scope picker.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/branches");
        setBranches(data || []);
      } catch { /* branches optional; if it 403s, workspace-only mode is fine */ }
    })();
  }, []);

  // Load config for the currently selected scope.
  const loadCfg = async (nextScope = scope) => {
    setLoaded(false);
    try {
      const endpoint = nextScope === "workspace"
        ? "/settings/student-number"
        : `/settings/student-number/branch/${nextScope}`;
      const [{ data: c }, { data: n }] = await Promise.all([
        api.get(endpoint),
        api.get("/students/next-number", {
          params: nextScope !== "workspace" ? { branch_id: nextScope } : {},
        }).catch(() => ({ data: {} })),
      ]);
      const cleaned = { ...DEFAULT_CFG, ...c };
      delete cleaned.is_override;
      setCfg(cleaned);
      setIsOverride(nextScope !== "workspace" && Boolean(c?.is_override));
      setNextNumber(n?.student_id || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoaded(true); }
  };
  useEffect(() => { loadCfg(scope); }, [scope]);

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const preview = useMemo(() => formatPreview(cfg, cfg.start_number || 1), [cfg]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = {
        prefix: (cfg.prefix || "").trim(),
        separator: cfg.separator,
        digits: Math.max(1, Math.min(8, parseInt(cfg.digits, 10) || 4)),
        include_year: !!cfg.include_year,
        start_number: Math.max(1, parseInt(cfg.start_number, 10) || 1),
        auto_generate: !!cfg.auto_generate,
      };
      const endpoint = scope === "workspace"
        ? "/settings/student-number"
        : `/settings/student-number/branch/${scope}`;
      const { data } = await api.put(endpoint, clean);
      const cleanData = { ...DEFAULT_CFG, ...data };
      delete cleanData.is_override;
      setCfg(cleanData);
      setIsOverride(scope !== "workspace" && Boolean(data?.is_override));
      // Refresh next-number hint.
      try {
        const { data: n } = await api.get("/students/next-number", {
          params: scope !== "workspace" ? { branch_id: scope } : {},
        });
        setNextNumber(n?.student_id || null);
      } catch { /* noop */ }
      toast.success("Student ID configuration saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const resetToWorkspaceDefault = async () => {
    if (scope === "workspace") return;
    if (!window.confirm("Remove this branch's override and fall back to the workspace default?")) return;
    setSaving(true);
    try {
      await api.delete(`/settings/student-number/branch/${scope}`);
      toast.success("Branch override removed — using workspace default now");
      await loadCfg(scope);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="studentnum-config-card">
      <CardHeader>
        <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
          <GraduationCap size={16} weight="duotone" />
          Student ID Format
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Configure the Student ID format for this workspace, or per branch.
          Student IDs are always <b>workspace-unique</b> — no two students share
          an ID even across branches. Deleting a student never frees the ID.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Scope picker */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Apply this format to</Label>
            <Select value={scope} onValueChange={setScope} disabled={!canEdit}>
              <SelectTrigger data-testid="studentnum-scope"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value="workspace">Workspace default</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-muted-foreground">
              {scope === "workspace"
                ? "Applies to every branch that doesn't have its own override."
                : isOverride
                  ? "This branch has a saved override. Editing will replace it."
                  : "This branch is using the workspace default. Save to create a branch-specific override."}
            </div>
          </div>
          {scope !== "workspace" && isOverride && (
            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={resetToWorkspaceDefault}
                disabled={saving || !canEdit}
                className="gap-1.5"
                data-testid="studentnum-reset-branch"
              >
                <Trash size={13} weight="duotone" /> Reset to workspace default
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Prefix</Label>
            <Input
              value={cfg.prefix}
              onChange={e => set("prefix", e.target.value.slice(0, 10).replace(/[^A-Za-z0-9_]/g, ""))}
              placeholder="STU"
              maxLength={10}
              disabled={!loaded || !canEdit}
              data-testid="studentnum-prefix"
            />
            <div className="text-[11px] text-muted-foreground">Letters and digits only, up to 10 characters (e.g. SUN, MOO, STU).</div>
          </div>
          <div className="space-y-1.5">
            <Label>Separator</Label>
            <Select
              value={cfg.separator === "" ? "none" : cfg.separator}
              onValueChange={v => set("separator", v === "none" ? "" : v)}
              disabled={!loaded || !canEdit}
            >
              <SelectTrigger data-testid="studentnum-separator"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card">
                {SEP_OPTIONS.map(o => (
                  <SelectItem key={o.label} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Digits</Label>
            <Input
              type="number" min={1} max={8}
              value={cfg.digits}
              onChange={e => set("digits", parseInt(e.target.value, 10) || 4)}
              disabled={!loaded || !canEdit}
              data-testid="studentnum-digits"
            />
            <div className="text-[11px] text-muted-foreground">e.g. 4 → 0001. Max 8 digits.</div>
          </div>
          <div className="space-y-1.5">
            <Label>Starting number</Label>
            <Input
              type="number" min={1}
              value={cfg.start_number}
              onChange={e => set("start_number", parseInt(e.target.value, 10) || 1)}
              disabled={!loaded || !canEdit}
              data-testid="studentnum-start"
            />
            <div className="text-[11px] text-muted-foreground">Auto-generation begins at this number.</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 rounded-md border p-3 cursor-pointer">
            <Switch
              checked={!!cfg.include_year}
              onCheckedChange={v => set("include_year", !!v)}
              disabled={!loaded || !canEdit}
              data-testid="studentnum-include-year"
            />
            <div className="text-sm">
              <div className="font-medium">Include current year</div>
              <div className="text-[11px] text-muted-foreground">Inserts the 4-digit year between the prefix and sequence, e.g. SUN-2026-0001.</div>
            </div>
          </label>
          <label className="flex items-center gap-3 rounded-md border p-3 cursor-pointer">
            <Switch
              checked={!!cfg.auto_generate}
              onCheckedChange={v => set("auto_generate", !!v)}
              disabled={!loaded || !canEdit}
              data-testid="studentnum-auto-generate"
            />
            <div className="text-sm">
              <div className="font-medium">Auto-generate on new student</div>
              <div className="text-[11px] text-muted-foreground">
                {cfg.auto_generate
                  ? "The next available Student ID pre-fills the Add Student form. Editable."
                  : "Admins must enter the Student ID manually when creating a student."}
              </div>
            </div>
          </label>
        </div>

        {/* Live preview */}
        <div className="rounded-md border-2 border-primary/30 bg-primary/5 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="studentnum-preview">
          <div>
            <div className="overline mb-1">Preview</div>
            <div className="font-mono text-2xl font-bold tabular-nums text-primary" data-testid="studentnum-preview-value">
              {preview}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Shape of a Student ID starting at {cfg.start_number || 1}.
            </div>
          </div>
          {nextNumber && (
            <div className="text-right">
              <div className="overline mb-1 flex items-center justify-end gap-1">
                <ArrowsClockwise size={11} /> Next in this workspace
              </div>
              <div className="font-mono text-lg font-semibold tabular-nums" data-testid="studentnum-next-value">
                {nextNumber}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={saving || !loaded || !canEdit}
            data-testid="studentnum-save"
            size="lg"
          >
            {saving ? "Saving…" : (scope === "workspace" ? "Save workspace default" : "Save branch override")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
